import React from "react";
import Paper from "@mui/material/Paper";
import dateFormat from "dateformat";
import AmCharts from "@amcharts/amcharts3-react";
import { chainTypeToString, getMaxGridCount, getWeekFromDateString } from "services/Utils";
import { injectIntl } from "react-intl";

class ProductPricePlot extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphs: [],
      plotData: [],
      product: {},
      activeStoresCount: 0,
      weeksCount: 5,
      id: this.props.product.id,
      fileName: this.props.product.name + " " + new Date().toJSON().slice(0, 10),
      plotLayout: {
        title: "",
        xaxis: {
          title: "",
        },
        yaxis: {
          title: "",
          ticksuffix: "",
        },
      },
    };
    this.chartInstance = null;
    this.legendItemEls = {};
    this.legendValueEls = {};
    this.cursorLocked = false;
    this.lockedCursorIndex = null;
    this.lockedCategory = null;
    this.lockedBulletItems = [];
    this._snappingCursor = false;
    this.chartListeners = [
      { event: "init", method: this.handleChartInit },
      { event: "changed", method: this.handleCursorChanged },
    ];
    this.cursorListeners = [{ event: "onHideCursor", method: this.handleCursorHidden }];
  }

  componentDidMount() {
    if (this.props.product) {
      this.parseDataForPlot();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.product !== this.props.product || prevProps.showDiscountPrices !== this.props.showDiscountPrices || prevProps.showComparePrices !== this.props.showComparePrices || this.props.product.getActiveStores().length !== this.state.activeStoresCount) {
      this.parseDataForPlot();
    }
  }
  static limitAverage = 10;

  unitConvertion = {
    1: "kg", // Weight units, converted to kilograms
    2: "l", // Volume units, converted to liters
    3: "st", // Singe units, not used for now
    4: "m", // Length units, converted to meters
  };

  parseDataForPlot = () => {
    const product = this.props.product;
    const activeStores = product.getActiveStores();
    const plotLayout = this.createLayoutForProduct(product);

    this.setState(
      {
        activeStoresCount: activeStores.length,
        product: product,
        plotLayout,
        plotData: this.createDataForStores(product, activeStores),
        graphs: this.getGraphs(activeStores, plotLayout),
      },
      () => {
        this.restoreCursorLock(this.chartInstance);
      },
    );
  };

  formatAmountToPrice(amount) {
    const amountFloat = parseFloat(amount);
    if (!isNaN(amountFloat)) {
      return (amountFloat / 100).toFixed(2);
    }
    return null;
  }

  formatDate(date) {
    return dateFormat(date, "yyyy-mm-dd");
  }

  getShortDateWithWeekLocale(dateString) {
    const isDk = this.props.intl.locale === "dk";
    const week = getWeekFromDateString(dateString);

    if (isDk) {
      // For Danish: convert YYYY-MM-DD to DD-MM-YY format
      const parts = dateString.split("-");
      const day = parts[2];
      const month = parts[1];
      const year = parts[0].substring(2); // Last 2 digits of year
      return day + "-" + month + "-" + year + "<br>v" + week;
    }
    // For other locales: use existing format (YY-MM-DD)
    return dateString.substring(2) + "<br>v" + week;
  }

  shouldAverageStore(activeStores, store) {
    const limit = ProductPricePlot.limitAverage;
    const sameChainStoresCount = activeStores.filter((s) => s.chain === store.chain && s.type === store.type).length;

    const allChainStores = this.props.product.getStore().filter((s) => s.chain === store.chain && s.type === store.type);
    const allSelected = allChainStores.length > 0 && sameChainStoresCount === allChainStores.length;

    return sameChainStoresCount > limit || (allChainStores.length <= limit && allSelected);
  }

  createDataForStores(product, activeStores) {
    const DELIMITER = "&";
    const chartDataDict = {};
    const dataDict = {};

    const unit = product.prices && product.prices.length > 0 && product.prices[0] && product.prices[0].length > 0 ? product.prices[0][0].unit : -1;
    const isUnitOk = unit !== 3 && unit !== 4 && product.prices.every((x) => x.every((y) => y.unit === unit));

    const priceDates = activeStores
      .map((store) => store.getProductPrices(product.id))
      .flatMap((cp) => cp)
      .flatMap((prices) => prices)
      .flatMap((p) => {
        if (p.lastDateTime) {
          const db = new Date(p.lastDateTime.getTime());
          db.setDate(db.getDate() - 1);
          return [p.datetime.getTime(), db.getTime(), p.lastDateTime.getTime()];
        }
        return [p.datetime.getTime()];
      })
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      })
      .map((x) => new Date(x))
      .sort((a, b) => {
        return a - b;
      });
    const weeks = new Set();
    activeStores.forEach((store) => {
      const avgValue = this.shouldAverageStore(activeStores, store);
      const innerProductPrices = store.getProductPricesFullRange(product.id, priceDates);
      innerProductPrices.forEach((prices, index) => {
        const chain = store.chain.toString();
        let dictKey = store.id + index;
        const chainName = chainTypeToString(store.type) != store.chainName ? store.chainName + " " + chainTypeToString(store.type) : store.chainName;

        let name = store.online ? store.name + " (Online)" : chainName + " " + store.name;
        if (avgValue) {
          dictKey = chain + DELIMITER + store.type;
          name = store.chainName || this.props.intl.formatMessage({ id: "store.other" });
          if (chainTypeToString(store.type) != name) {
            name += " " + chainTypeToString(store.type);
          }
        }

        chartDataDict[dictKey] = chartDataDict[dictKey] || {
          name: name,
          visible: store.active,
          color: store.color,
        };

        dataDict[dictKey] = dataDict[dictKey] || {};

        prices.forEach((price) => {
          const date = price.date;
          const ret = dataDict[dictKey][date] || {
            prices: null,
            count: 0,
            discount: false,
            onlyMembers: false,
          };

          let addCount = false;

          if (price.discountAmount && this.props.showDiscountPrices) {
            if (price.discountAmount !== null) {
              if (this.props.showComparePrices && isUnitOk && price.compareDiscountAmount !== null) {
                ret.prices += price.compareDiscountAmount;
              } else {
                ret.prices += price.discountAmount;
              }
              addCount = true;
            }
            ret.onlyMembers = ret.onlyMembers || price.onlyMembers;
            ret.discount = true;
          } else if (price.amount !== null) {
            if (this.props.showComparePrices && isUnitOk) {
              ret.prices += price.compareAmount;
            } else {
              ret.prices += price.amount;
            }
            addCount = true;
          }

          if (addCount) {
            ret.count++;
          }
          dataDict[dictKey][date] = ret;
        });

        const x = Object.keys(dataDict[dictKey]);
        const y = [];
        const text = [];

        x.forEach((date) => {
          weeks.add(date.substring(0, 4) + getWeekFromDateString(date));
          const elem = dataDict[dictKey][date];
          let tempText = "";

          if (elem.discount) {
            tempText = this.props.intl.formatMessage({ id: "priceTable.campaignPriceExclamation" }) + " ";
            if (elem.onlyMembers) {
              tempText += this.props.intl.formatMessage({ id: "priceTable.memberOnly" }) + " ";
            }
          }

          if (elem.count > 1) {
            tempText = "(" + elem.count + " " + this.props.intl.formatMessage({ id: "priceTable.multipleStores" }) + ")";
          }

          text.push(tempText);

          const price = this.formatAmountToPrice(elem.prices / elem.count);
          y.push(price);
        });

        chartDataDict[dictKey].y = y;
        chartDataDict[dictKey].x = x;
        chartDataDict[dictKey].text = text;
      });
    });

    const chartData = Object.keys(chartDataDict)
      .map((key) => chartDataDict[key])
      .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    const result = this.createChartData(chartData);
    this.setState({
      weeksCount: weeks.size,
    });
    return result;
  }

  createChartData(chartData) {
    const map = new Map();

    for (const data of chartData) {
      this.createChartDataHelper(data, map);
    }
    const result = [];
    map.forEach((value, key) => {
      if (value !== null) {
        result.push({ date: key, ...value });
      } else {
        result.push({ date: key });
      }
    });
    return result;
  }

  createChartDataHelper(data, map) {
    for (let i = 0; i < data.x.length; i++) {
      if (data.y[i] !== null) {
        if (map.has(data.x[i])) {
          const currentValue = map.get(data.x[i]);
          const valueToAdd = { [data.name.split(" ").join("")]: +data.y[i] };
          const newValue = { ...currentValue, ...valueToAdd };
          map.set(data.x[i], newValue);
        } else {
          map.set(data.x[i], { [data.name.split(" ").join("")]: +data.y[i] });
        }
      } else if (!map.has(data.x[i])) {
        map.set(data.x[i]);
      }
    }
  }

  createLayoutForProduct(product) {
    const unit = product.prices && product.prices.length > 0 && product.prices[0] && product.prices[0].length > 0 ? product.prices[0][0].unit : -1;
    const isUnitOk = unit !== 3 && unit !== 4 && product.prices.every((x) => x.every((y) => y.unit === unit));
    let layout;
    if (this.props.showComparePrices && isUnitOk) {
      layout = this.getStandardLayout(unit);
      layout.title = product.name + " - " + this.props.intl.formatMessage({ id: "priceTable.showsComparePrice" });
    } else {
      layout = this.getStandardLayout();
      layout.title = product.name;
    }
    return layout;
  }

  getStandardLayout = (unit) => {
    const margins = 48;
    const layout = {
      autosize: true,
      width: "98%",

      height: this.props.height || 400,
      showlegend: true,
      margin: {
        r: margins,
        l: margins + 32,
        b: margins,
        t: margins + 48,
        pad: 1,
      },
      xaxis: {
        title: this.props.intl.formatMessage({ id: "priceTable.date" }),
      },
    };
    if (unit) {
      layout.yaxis = {
        title: this.props.intl.formatMessage({ id: "priceTable.comparePrice" }),
        ticksuffix: " kr/" + this.unitConvertion[unit],
      };
    } else {
      layout.yaxis = {
        title: this.props.intl.formatMessage({ id: "priceTable.price" }),
        ticksuffix: " kr",
      };
    }
    return layout;
  };

  getGraphs(activeStores, plotLayout) {
    const datas = activeStores.filter((activeStore) => activeStore.active).reduce((a, b) => ({ ...a, [this.getStoreId(activeStores, b)]: b.color }), {});
    return Object.entries(datas)
      .sort((a, b) => (a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0))
      .map(([storeId, color]) => this.createGraph(storeId, color, plotLayout));
  }

  getStoreId = (activeStores, store) => {
    const avgValue = this.shouldAverageStore(activeStores, store);

    let chain = store.chainName || this.props.intl.formatMessage({ id: "store.other" });
    if (chainTypeToString(store.type) != chain) {
      chain += " " + chainTypeToString(store.type);
    }
    return avgValue ? chain : store.online ? store.name + " (Online)" : chain + " " + store.name;
  };

  createGraph(storeId, color, plotLayout) {
    return {
      id: "graph" + storeId.split(" ").join(""),
      balloon: {
        verticalPadding: 3,
      },
      balloonText: "<b>" + storeId + "</b>: [[value]] " + plotLayout.yaxis.ticksuffix,
      type: "step",
      lineThickness: 2,
      lineColor: color,
      bullet: "square",
      bulletAlpha: 0,
      bulletSize: 2,
      bulletBorderAlpha: 0,
      title: storeId,
      valueField: storeId.split(" ").join(""),
      connect: false,
    };
  }

  handleChartInit = (e) => {
    this.chartInstance = e.chart;
    this.lockedBulletItems = [];
    if (e.chart.chartDiv && !e.chart.chartDiv._cursorLockBound) {
      e.chart.chartDiv._cursorLockBound = true;
      e.chart.chartDiv.addEventListener("click", this.handleChartAreaClick);
      e.chart.chartDiv.addEventListener("mouseleave", this.handleChartMouseLeave);
    }
    this.restoreCursorLock(e.chart);
  };

  restoreCursorLock = (chart) => {
    if (!this.cursorLocked || !this.lockedCategory || !chart) {
      this.lockedBulletItems = [];
      return;
    }

    const dataProvider = chart.dataProvider || this.state.plotData || [];
    const index = dataProvider.findIndex((item) => item && item.date === this.lockedCategory);
    if (index < 0) {
      this.cursorLocked = false;
      this.lockedCursorIndex = null;
      this.lockedCategory = null;
      this.lockedBulletItems = [];
      this.setLegendValues({});
      return;
    }

    this.lockedCursorIndex = index;
    this.lockedBulletItems = [];
    if (chart.chartCursor) {
      chart.chartCursor.leaveCursor = true;
      chart.chartCursor.forceShow = true;
      try {
        chart.chartCursor.showCursorAt(this.lockedCategory);
      } catch (err) {
        // Chart may not be fully ready
      }
    }
    this.updateLegendFromIndex(chart, index);
    requestAnimationFrame(() => {
      this.applyLockedBullets(chart);
      this.hideHoverBulletsExceptLocked(chart);
    });
  };

  setLegendValues = (valuesById) => {
    Object.keys(this.legendValueEls).forEach((graphId) => {
      const el = this.legendValueEls[graphId];
      if (el) {
        el.textContent = valuesById[graphId] || "";
      }
    });
  };

  resetBulletAppearance = (dItem, bullet) => {
    if (!bullet || !dItem) {
      return;
    }
    const graph = dItem.graph;
    const alpha = graph && !isNaN(graph.bulletAlpha) ? graph.bulletAlpha : 0;
    const borderAlpha = graph && !isNaN(graph.bulletBorderAlpha) ? graph.bulletBorderAlpha : 0;
    bullet.doNotScale = false;
    try {
      bullet.translate(dItem.bx, dItem.by, 1);
      bullet.setAttr("fill-opacity", alpha);
      bullet.setAttr("stroke-opacity", borderAlpha);
    } catch (err) {
      // Chart may have been redrawn
    }
  };

  clearLockedBullets = () => {
    (this.lockedBulletItems || []).forEach(({ dItem, bullet }) => {
      this.resetBulletAppearance(dItem, bullet);
    });
    this.lockedBulletItems = [];
  };

  hideHoverBulletsExceptLocked = (chart) => {
    if (!chart) {
      return;
    }
    if (chart.chartCursor && typeof chart.chartCursor.fixBulletSize === "function") {
      chart.chartCursor.fixBulletSize();
    }
    const lockedBullets = new Set((this.lockedBulletItems || []).map((item) => item.bullet));
    if (!chart.chartData) {
      return;
    }
    chart.chartData.forEach((serialDataItem) => {
      const axes = (serialDataItem && serialDataItem.axes) || {};
      Object.keys(axes).forEach((axisId) => {
        const graphsMap = axes[axisId].graphs || {};
        Object.keys(graphsMap).forEach((graphId) => {
          const dItem = graphsMap[graphId];
          if (!dItem || !dItem.bulletGraphics || lockedBullets.has(dItem.bulletGraphics)) {
            return;
          }
          this.resetBulletAppearance(dItem, dItem.bulletGraphics);
        });
      });
    });
  };

  hideHoverBalloonsExceptLocked = (chart) => {
    if (!chart) {
      return;
    }
    (chart.graphs || []).forEach((graph) => {
      if (graph && typeof graph.hideBalloonReal === "function") {
        graph.hideBalloonReal();
      } else if (graph && typeof graph.hideBalloon === "function") {
        graph.hideBalloon();
      }
    });
    if (typeof chart.hideBalloon === "function") {
      chart.hideBalloon();
    } else if (chart.balloon && typeof chart.balloon.hide === "function") {
      chart.balloon.hide();
    }
    if (this.cursorLocked && chart.chartCursor && this.lockedCategory != null) {
      this._snappingCursor = true;
      chart.chartCursor.forceShow = true;
      chart.chartCursor.showCursorAt(this.lockedCategory);
      this._snappingCursor = false;
    }
  };

  applyLockedBullets = (chart) => {
    const index = this.lockedCursorIndex;
    if (index == null || !chart || !chart.chartData || !chart.chartData[index]) {
      return;
    }
    const scale = (chart.chartCursor && chart.chartCursor.graphBulletSize) || 1.7;

    if (!this.lockedBulletItems.length) {
      const axes = chart.chartData[index].axes || {};
      Object.keys(axes).forEach((axisId) => {
        const graphsMap = axes[axisId].graphs || {};
        Object.keys(graphsMap).forEach((graphId) => {
          const dItem = graphsMap[graphId];
          if (!dItem || !dItem.bulletGraphics || (dItem.graph && dItem.graph.hidden)) {
            return;
          }
          this.lockedBulletItems.push({ dItem, bullet: dItem.bulletGraphics });
        });
      });
    }

    this.lockedBulletItems.forEach(({ dItem, bullet }) => {
      if (!bullet || !dItem) {
        return;
      }
      bullet.doNotScale = true;
      try {
        bullet.translate(dItem.bx, dItem.by, scale);
        bullet.setAttr("fill-opacity", 1);
        bullet.setAttr("stroke-opacity", 1);
      } catch (err) {
        // Chart may have been redrawn
      }
    });
  };

  updateLegendFromIndex = (chart, index) => {
    if (index == null || !chart || !chart.dataProvider) {
      this.setLegendValues({});
      return;
    }
    const dataItem = chart.dataProvider[index];
    if (!dataItem) {
      this.setLegendValues({});
      return;
    }
    const ticksuffix = (this.state.plotLayout.yaxis && this.state.plotLayout.yaxis.ticksuffix) || "";
    const legendValues = {};
    (this.state.graphs || []).forEach((graph) => {
      const value = dataItem[graph.valueField];
      if (value != null && value !== "") {
        legendValues[graph.id] = value + ticksuffix;
      }
    });
    this.setLegendValues(legendValues);
  };

  handleChartAreaClick = () => {
    const chart = this.chartInstance;
    if (!chart || !chart.chartCursor) {
      return;
    }

    if (this.cursorLocked) {
      this.cursorLocked = false;
      this.lockedCursorIndex = null;
      this.lockedCategory = null;
      chart.chartCursor.leaveCursor = false;
      this.clearLockedBullets();
      this.hideHoverBulletsExceptLocked(chart);
      this.hideHoverBalloonsExceptLocked(chart);
      return;
    }

    const index = chart.chartCursor.index;
    if (index == null || !chart.dataProvider || !chart.dataProvider[index]) {
      return;
    }

    this.cursorLocked = true;
    this.lockedCursorIndex = index;
    this.lockedCategory = chart.dataProvider[index].date;
    chart.chartCursor.leaveCursor = true;
    this.updateLegendFromIndex(chart, index);
    this.applyLockedBullets(chart);
    this.hideHoverBulletsExceptLocked(chart);
  };

  handleChartMouseLeave = () => {
    const chart = this.chartInstance;
    if (!chart) {
      return;
    }
    this.hideHoverBulletsExceptLocked(chart);
    this.hideHoverBalloonsExceptLocked(chart);
    if (this.cursorLocked) {
      this.applyLockedBullets(chart);
      return;
    }
    this.setLegendValues({});
  };

  handleCursorChanged = (e) => {
    if (this.cursorLocked) {
      if (!this._snappingCursor && this.lockedCategory != null && e.index !== this.lockedCursorIndex && e.chart && e.chart.chartCursor) {
        this._snappingCursor = true;
        e.chart.chartCursor.showCursorAt(this.lockedCategory);
        this._snappingCursor = false;
      }
      if (e.chart && e.chart.chartCursor && typeof e.chart.chartCursor.fixBulletSize === "function") {
        e.chart.chartCursor.fixBulletSize();
      }
      this.applyLockedBullets(e.chart);
      return;
    }
    this.updateLegendFromIndex(e.chart, e.index);
  };

  handleCursorHidden = () => {
    this.handleChartMouseLeave();
  };

  toggleLegendGraph = (graphId) => {
    const chart = this.chartInstance;
    if (!chart || !chart.graphs) {
      return;
    }
    const graph = chart.graphs.find((item) => item.id === graphId);
    if (!graph) {
      return;
    }
    const willHide = !graph.hidden;
    if (willHide) {
      chart.hideGraph(graph);
    } else {
      chart.showGraph(graph);
    }
    const itemEl = this.legendItemEls[graphId];
    if (itemEl) {
      itemEl.classList.toggle("is-hidden", willHide);
    }
  };

  getVisibleExportGraphs = () => {
    return (this.state.graphs || []).filter((graph) => {
      const chartGraph = this.chartInstance && this.chartInstance.graphs && this.chartInstance.graphs.find((item) => item.id === graph.id);
      return !(chartGraph && chartGraph.hidden);
    });
  };

  getExportLegendValues = () => {
    const chart = this.chartInstance;
    let index = null;
    if (this.cursorLocked && this.lockedCursorIndex != null) {
      index = this.lockedCursorIndex;
    } else if (chart && chart.chartCursor && chart.chartCursor.index != null) {
      index = chart.chartCursor.index;
    } else if (chart && chart.dataProvider && chart.dataProvider.length) {
      index = chart.dataProvider.length - 1;
    }

    if (index != null && chart && chart.dataProvider && chart.dataProvider[index]) {
      const dataItem = chart.dataProvider[index];
      const ticksuffix = (this.state.plotLayout.yaxis && this.state.plotLayout.yaxis.ticksuffix) || "";
      const legendValues = {};
      (this.state.graphs || []).forEach((graph) => {
        const value = dataItem[graph.valueField];
        if (value != null && value !== "") {
          legendValues[graph.id] = value + ticksuffix;
        }
      });
      return legendValues;
    }

    const legendValues = {};
    Object.keys(this.legendValueEls || {}).forEach((graphId) => {
      const el = this.legendValueEls[graphId];
      if (el && el.textContent) {
        legendValues[graphId] = el.textContent;
      }
    });
    return legendValues;
  };

  addLegendToFabricExport = (exportPlugin) => {
    const fabricCanvas = exportPlugin.setup.fabric;
    const chartWidth = fabricCanvas.getWidth();
    const chartHeight = fabricCanvas.getHeight();
    const labelOffset = 28;
    const priceGap = 8;
    const rightPadding = 6;
    const minLegendWidth = 240;
    const lineHeight = 16;
    const graphs = this.getVisibleExportGraphs();
    const legendValues = this.getExportLegendValues();
    const neededHeight = Math.max(chartHeight, 40 + graphs.length * lineHeight);
    const fontFamily = (exportPlugin.setup.chart && exportPlugin.setup.chart.fontFamily) || "Arial";
    const fontSize = 11;

    const cleanLabel = (title) =>
      String(title || "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]+>/g, "");

    let maxLabelWidth = 0;
    let maxPriceWidth = 0;
    const rows = graphs.map((graph) => {
      const label = cleanLabel(graph.title);
      maxLabelWidth = Math.max(maxLabelWidth, new fabric.Text(label, { fontSize, fontFamily }).width);
      const price = legendValues[graph.id] || "";
      if (price) {
        maxPriceWidth = Math.max(maxPriceWidth, new fabric.Text(price, { fontSize, fontFamily }).width);
      }
      return { graph, label, price };
    });

    const legendWidth = Math.max(minLegendWidth, labelOffset + maxLabelWidth + priceGap + maxPriceWidth + rightPadding);
    const labelLeft = chartWidth + labelOffset;

    fabricCanvas.setWidth(chartWidth + legendWidth);
    fabricCanvas.setHeight(neededHeight);

    let y = 40;
    rows.forEach(({ graph, label, price }) => {
      fabricCanvas.add(
        new fabric.Line([chartWidth + 6, y + 7, chartWidth + 20, y + 7], {
          stroke: graph.lineColor || "#999",
          strokeWidth: 3,
          selectable: false,
        }),
      );
      fabricCanvas.add(
        new fabric.Text(label, {
          left: labelLeft,
          top: y,
          fontSize,
          fontFamily,
          fill: "#000000",
          selectable: false,
        }),
      );
      if (price) {
        fabricCanvas.add(
          new fabric.Text(price, {
            left: chartWidth + legendWidth - rightPadding,
            top: y,
            fontSize,
            fontFamily,
            fill: "#000000",
            originX: "right",
            selectable: false,
          }),
        );
      }
      y += lineHeight;
    });
  };

  renderHtmlLegend(chartHeight) {
    const graphs = this.state.graphs || [];
    return (
      <div className="chart-legend-scroll" style={{ height: chartHeight, maxHeight: chartHeight }}>
        {graphs.map((graph) => (
          <div
            className="chart-legend-item"
            key={graph.id || graph.title}
            ref={(el) => {
              this.legendItemEls[graph.id] = el;
            }}
            onClick={() => this.toggleLegendGraph(graph.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(evt) => {
              if (evt.key === "Enter" || evt.key === " ") {
                evt.preventDefault();
                this.toggleLegendGraph(graph.id);
              }
            }}>
            <span className="chart-legend-marker" style={{ backgroundColor: graph.lineColor || "#999" }} />
            <span className="chart-legend-label">{graph.title}</span>
            <span
              className="chart-legend-value"
              ref={(el) => {
                this.legendValueEls[graph.id] = el;
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  render() {
    const id = this.state.id;
    const exportMenuId = `export-menu-${id}`;
    const fileName = this.state.fileName;
    const weekLabel = this.props.intl.formatMessage({ id: "priceTable.week" });
    const isDk = this.props.intl.locale === "dk";
    const chartDateFormat = isDk ? "DD-MM-YYYY" : "YYYY-MM-DD";
    const chartHeight = this.state.plotLayout.height || 400;
    const plot = this;
    return (
      <Paper elavation={0}>
        <div className="chart-with-legend">
          {this.renderHtmlLegend(Math.max(chartHeight - 52, 120))}
          <div className="chart-with-legend-plot">
            <AmCharts.React
              style={{
                width: "100%",
                height: chartHeight,
              }}
              options={{
                type: "serial",
                hideCredits: true,
                titles: [{ text: this.state.plotLayout.title }],
                theme: "light",
                autoMarginOffset: 25,
                dataProvider: this.state.plotData,
                graphs: this.state.graphs,
                listeners: this.chartListeners,
                chartCursor: {
                  fullWidth: false,
                  cursorAlpha: 0.05,
                  graphBulletAlpha: 1,
                  oneBalloonOnly: true,
                  listeners: this.cursorListeners,
                },
                dataDateFormat: chartDateFormat,
                categoryField: "date",
                categoryAxis: {
                  minPeriod: "DD",
                  parseDates: false,
                  autoGridCount: false,
                  showFirstLabel: true,
                  showLastLabel: true,
                  equalSpacing: false,
                  maximumDate: "2018-11-12",
                  minimumDate: "2018-10-12",
                  gridCount: Math.min(this.state.weeksCount - 2, getMaxGridCount()),
                  categoryFunction: (dateString) => this.getShortDateWithWeekLocale(dateString),
                  gridAlpha: 0,
                  dateFormats: [
                    {
                      period: "DD",
                      format: chartDateFormat,
                    },
                    {
                      period: "WW",
                      format: chartDateFormat,
                    },
                    {
                      period: "MM",
                      format: "MMM YYYY",
                    },
                    {
                      period: "YYYY",
                      format: "MMM YYYY",
                    },
                  ],
                },
                valueAxes: [
                  {
                    position: "left",
                    title: this.state.plotLayout.yaxis.title,
                    unit: this.state.plotLayout.yaxis.ticksuffix,
                    axisAlpha: 0,
                  },
                ],
                export: {
                  enabled: true,
                  fileName: fileName,
                  divId: exportMenuId,
                  removeImages: false,
                  forceRemoveImages: false,
                  position: "bottom-right",
                  menu: [
                    {
                      class: "export-main",
                      menu: [
                        { label: "Download", menu: ["PNG", "JPG", "CSV", "XLSX", "PDF"] },
                        {
                          label: "Annotate",
                          action: "draw",
                          menu: [{ class: "export-drawing", menu: ["PNG", "JPG", "CSV", "XLSX", "PDF", "PRINT", "CANCEL"] }],
                        },
                        { label: "Print", format: "PRINT" },
                      ],
                    },
                    {
                      class: "export-png",
                      label: "PNG",
                      title: "Download PNG",
                      format: "PNG",
                    },
                  ],
                  afterCapture: function afterCapture(menuConfig) {
                    const chartWidthBeforeLegend = this.setup.fabric.getWidth();
                    plot.addLegendToFabricExport(this);

                    const imgFromHtml = document.getElementById(id);
                    if (imgFromHtml) {
                      imgFromHtml.crossOrigin = "anonymous";
                      const fabricImage = new fabric.Image(imgFromHtml, {
                        top: 7,
                        left: chartWidthBeforeLegend * 0.8,
                      });
                      this.setup.fabric.add(fabricImage);
                    }

                    const weekNumber = (today) => {
                      const onejan = new Date(today.getFullYear(), 0, 1);
                      return Math.ceil(((today - onejan) / 86400000 + onejan.getDay() + 1) / 7);
                    };
                    const text = new fabric.Text(weekLabel + " " + weekNumber(new Date()), {
                      top: 15,
                      left: 15,
                      fontFamily: this.setup.chart.fontFamily,
                      fontWeight: "bold",
                      fontSize: 15,
                    });
                    this.setup.fabric.add(text);
                  },
                },
              }}
            />
          </div>
          <div id={exportMenuId} className="chart-export-menu-host" />
        </div>
      </Paper>
    );
  }
}

export default injectIntl(ProductPricePlot);
